import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook for managing camera stream lifecycle
 * @param {Object} options
 * @param {boolean} options.enabled - Whether camera should be active
 * @param {string} options.facingMode - 'user' (front) or 'environment' (back)
 * @returns {Object} { stream, error, devices, videoRef, switchCamera }
 */
export const useCameraStream = ({ enabled = true, facingMode = 'environment' } = {}) => {
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  const [devices, setDevices] = useState([]);
  const [currentFacingMode, setCurrentFacingMode] = useState(facingMode);
  const [isLoading, setIsLoading] = useState(false);
  const videoRef = useRef(null);

  // Stop current stream
  const stopStream = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  }, [stream]);

  // Initialize camera
  const initCamera = useCallback(async (facing) => {
    try {
      setIsLoading(true);
      setError(null);

      // Enumerate devices first
      const deviceList = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = deviceList.filter(d => d.kind === 'videoinput');
      setDevices(videoDevices);

      // Request camera access
      // Try with facingMode first, fallback to any camera if it fails
      let mediaStream;
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: facing,
            width: { ideal: 1280, min: 640 },
            height: { ideal: 720, min: 480 }
          },
          audio: false
        });
      } catch (facingError) {
        console.warn(`FacingMode '${facing}' failed, trying any camera:`, facingError.message);
        // Fallback: try without facingMode constraint (use default/any camera)
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1280, min: 640 },
            height: { ideal: 720, min: 480 }
          },
          audio: false
        });
      }

      setStream(mediaStream);

      // Attach to video element if ref is available
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        // Ensure video plays (some browsers need explicit play call)
        try {
          await videoRef.current.play();
        } catch (playErr) {
          console.warn('Video autoplay failed, user interaction may be required:', playErr);
        }
      }

      setIsLoading(false);
    } catch (err) {
      setError(err.message || 'Failed to access camera');
      setIsLoading(false);
      console.error('Camera access error:', err);
    }
  }, []);

  // Switch between front and back camera
  const switchCamera = useCallback(() => {
    stopStream();
    const newFacing = currentFacingMode === 'user' ? 'environment' : 'user';
    setCurrentFacingMode(newFacing);
    initCamera(newFacing);
  }, [currentFacingMode, stopStream, initCamera]);

  // Initialize on mount when enabled
  useEffect(() => {
    if (enabled) {
      initCamera(currentFacingMode);
    } else {
      stopStream();
    }

    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [enabled]);

  // Update video element when stream changes
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
      // Ensure video plays
      videoRef.current.play().catch(err => {
        console.warn('Video play failed:', err);
      });
    }
  }, [stream]);

  return {
    stream,
    error,
    devices,
    videoRef,
    isLoading,
    currentFacingMode,
    switchCamera,
    stopStream,
    reinitialize: () => initCamera(currentFacingMode)
  };
};

export default useCameraStream;
